import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { openAnalyticsPreferences } from './AnalyticsConsent';

const Footer: React.FC = () => {
  const { pathname } = useLocation();
  const facebookUrl = 'https://www.facebook.com/profile.php?id=61594069539972';
  const instagramUrl = 'https://www.instagram.com/certy.app/';
  const footerLinks = {
    producto: [
      { name: 'Características', href: '/#features', internal: false },
      { name: 'Precios', href: '/#pricing', internal: false },
      { name: 'Descargar', href: '/downloads', internal: true },
      { name: 'Documentación', href: '/docs', internal: true },
      { name: 'Qué es Certy', href: '/sobre-certy', internal: true },
      { name: 'Ficha del software', href: '/software-gestion-certificados-digitales', internal: true }
    ],
    recursos: [
      { name: 'Guías', href: '/guias', internal: true },
      { name: 'Blog', href: '/blog', internal: true },
      { name: 'Solucionar errores', href: '/recursos/errores', internal: true },
      { name: 'Herramientas', href: '/herramientas', internal: true },
      { name: 'Portales', href: '/portales', internal: true },
      { name: 'Plantillas', href: '/plantillas', internal: true },
      { name: 'Glosario', href: '/glosario', internal: true }
    ],
    soluciones: [
      { name: 'Todas las soluciones', href: '/soluciones', internal: true },
      { name: 'Buscar certificados', href: '/buscar-certificados-digitales-en-windows', internal: true },
      { name: 'Control de caducidad', href: '/control-caducidad-certificados-digitales', internal: true },
      { name: 'Para gestorías', href: '/para/gestorias', internal: true },
      { name: 'Para abogados', href: '/para/abogados', internal: true },
      { name: 'Para asesorías', href: '/para/asesorias-fiscales', internal: true },
      { name: 'Para equipos laborales', href: '/para/departamentos-laborales', internal: true },
      { name: 'Para admin. fincas', href: '/para/administradores-de-fincas', internal: true },
      { name: 'Para notarías', href: '/para/notarias', internal: true },
      { name: 'Para PYMEs', href: '/para/pymes', internal: true }
    ]
  };

  const renderLink = (link: { name: string; href: string; internal: boolean }) => {
    if (link.internal) {
      return (
        <Link to={link.href} className="hover:text-certy-green transition-colors">
          {link.name}
        </Link>
      );
    }

    return (
      <a href={link.href} className="hover:text-certy-green transition-colors">
        {link.name}
      </a>
    );
  };

  if (pathname.replace(/\/$/, '') === '/downloads') {
    return (
      <footer className="bg-[#07110d] py-7 text-white">
        <div className="mx-auto flex max-w-7xl flex-col gap-5 px-5 sm:px-8 md:flex-row md:items-center md:justify-between">
          <p className="text-sm text-[#c9d7ce]">Certy · Gestión de certificados digitales para Windows.</p>
          <nav aria-label="Enlaces de cierre" className="flex flex-wrap gap-x-5 gap-y-3 text-sm text-[#c9d7ce]">
            <a href="mailto:contacto@certy.es" className="hover:text-white">Soporte</a>
            <Link to="/privacidad" className="hover:text-white">Privacidad</Link>
            <Link to="/aviso-legal" className="hover:text-white">Aviso legal</Link>
            <button type="button" onClick={openAnalyticsPreferences} className="hover:text-white">Configurar cookies</button>
          </nav>
        </div>
      </footer>
    );
  }

  if (pathname === '/') {
    return (
      <footer className="border-t border-white/15 bg-black py-10 text-white">
        <div className="mx-auto flex max-w-[1240px] flex-col gap-7 px-5 sm:px-8 md:flex-row md:items-center md:justify-between lg:px-10">
          <div className="flex items-center gap-3">
            <img src="/certy-logo-96.png" alt="Certy" width="36" height="36" loading="lazy" className="h-9 w-9" />
            <div>
              <p className="font-semibold">Certy</p>
              <p className="text-sm text-gray-400">Gestión de certificados digitales para Windows.</p>
            </div>
          </div>
          <nav aria-label="Enlaces de cierre" className="flex flex-wrap gap-x-5 gap-y-3 text-sm text-gray-300">
            <a href="/#features" className="hover:text-white">Producto</a>
            <a href="/#pricing" className="hover:text-white">Precios</a>
            <Link to="/docs" className="hover:text-white">Documentación</Link>
            <Link to="/herramientas" className="hover:text-white">Herramientas</Link>
            <a href="mailto:contacto@certy.es" className="hover:text-white">Soporte</a>
            <a href={facebookUrl} target="_blank" rel="noreferrer" className="hover:text-white">Facebook</a>
            <a href={instagramUrl} target="_blank" rel="noreferrer" className="hover:text-white">Instagram</a>
            <Link to="/privacidad" className="hover:text-white">Privacidad</Link>
            <Link to="/aviso-legal" className="hover:text-white">Aviso legal</Link>
            <button type="button" onClick={openAnalyticsPreferences} className="hover:text-white">Configurar cookies</button>
          </nav>
        </div>
      </footer>
    );
  }

  return (
    <footer className="bg-black text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <div className="flex items-center space-x-3 mb-4">
              <img src="/certy-logo-96.png" alt="Certy" width="40" height="40" loading="lazy" className="w-10 h-10" />
              <span className="text-2xl font-bold">Certy</span>
            </div>
            <p className="text-gray-400">
              Software para gestionar certificados digitales en equipos profesionales y acceder a
              portales oficiales con menos incidencias.
            </p>
          </div>

          <div>
            <h3 className="font-semibold mb-4">Producto</h3>
            <ul className="space-y-2 text-gray-400">
              {footerLinks.producto.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">Recursos</h3>
            <ul className="space-y-2 text-gray-400">
              {footerLinks.recursos.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>

          <div>
            <h3 className="font-semibold mb-4">Soluciones</h3>
            <ul className="space-y-2 text-gray-400">
              {footerLinks.soluciones.map((link) => (
                <li key={link.name}>{renderLink(link)}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-gray-400">
          <p>
            &copy; 2026 <span className="text-certy-green font-semibold">Certy</span>. Gestión profesional
            de certificados digitales para equipos.
          </p>
          <p className="mt-2 text-xs text-gray-500">Soporte: contacto@certy.es</p>
          <nav aria-label="Información legal" className="mt-4 flex flex-wrap justify-center gap-5 text-xs">
            <Link to="/privacidad" className="hover:text-white transition-colors">Privacidad</Link>
            <Link to="/aviso-legal" className="hover:text-white transition-colors">Aviso legal</Link>
            <a href={facebookUrl} target="_blank" rel="noreferrer" className="hover:text-white transition-colors">Facebook</a>
            <a href={instagramUrl} target="_blank" rel="noreferrer" className="hover:text-white transition-colors">Instagram</a>
            <button type="button" onClick={openAnalyticsPreferences} className="hover:text-white transition-colors">
              Configurar cookies
            </button>
          </nav>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
